<template>
  <home-template>
    <div class="merchant-container" slot="content">
      <div class="merchat-top-container">
        <v-row class="merchat-top-container-title-label">
          <div class="text-title">{{ currentMerchantName }}</div>
        </v-row>
        <v-row class="tabs tabs-with-paddings">
          <v-tabs v-model="tab">
            <v-tab
              v-for="(item, index) in tabs"
              :class="{ 'pl-0': index === 0 }"
              :key="item.key"
              v-show="item.key === 'parkings' ? hasParkingResults : !item.hideTab"
            >
              {{ item.title }}
            </v-tab>
            <v-col class="button-panel">
              <v-row>
                <v-spacer />
                <v-btn
                  v-if="auth.isParkmanAdmin() || auth.isMerchantAdmin()"
                  class="pkmn-button add-user"
                  color="primary"
                  v-show="isUsersTab"
                  @click="openAddUserForm"
                >
                  Add new user
                </v-btn>
                <v-btn
                  class="pkmn-button add-report-id mr-2"
                  color="primary"
                  outlined
                  v-show="isReportsTab && auth.isParkmanAdmin()"
                  @click="openSetReportRecipientsPopup"
                >
                  {{ $t('set_report_recipients') }} ({{
                    recipientsList.length || 0
                  }})
                </v-btn>
                <div class="three-buttons-gap">
                  <period-picker
                  v-if="isReportsTab"
                  :isDatePicker="false"
                  :initialStartDate="payoutsStartDate"
                  :initialEndDate="payoutsEndDate"
                  :minStartDate="'2025-02-01'"
                  @periodUpdated="loadReports"
                />
                </div>
                <v-btn
                  class="pkmn-button add-report-id"
                  color="primary"
                  v-show="isReportsTab && auth.isParkmanAdmin()"
                  @click="openSetReportForm"
                >
                  {{ $t('report_settings') }}
                </v-btn>
                <v-btn
                  class="pkmn-button discount-button mr-2"
                  color="primary"
                  outlined
                  v-show="
                    isDiscountsTab &&
                    (auth.isParkmanAdmin() || auth.isMerchantAdmin())
                  "
                  @click="openCreateVehicleDiscountForm"
                >
                  Vehicle based discount
                  <v-icon right>mdi-chevron-right</v-icon>
                </v-btn>
                <v-btn
                  class="pkmn-button discount-button"
                  color="primary"
                  outlined
                  v-show="
                    isDiscountsTab &&
                    (auth.isParkmanAdmin() || auth.isMerchantAdmin())
                  "
                  @click="openCreateDiscountForm"
                >
                  Day Based Discount
                  <v-icon right>mdi-chevron-right</v-icon>
                </v-btn>
              </v-row>
            </v-col>
          </v-tabs>
        </v-row>
      </div>
      <v-row class="custom-row">
        <v-tabs-items v-model="tab">
          <v-tab-item class="tab-item map-tab">
            <v-divider class="with-horizontal-padding--map" />
            <tab-map
              :zones="zones"
              :merchant="currentMerchant"
              :shouldZoomToCenter="shouldZoomToCenter"
              :focusedPoint="focusedPoint"
            />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider />
            <tab-sales
              v-if="isSalesTab"
              :data="salesData"
              @periodUpdated="updatePeriodDates"
              @export="exportSalesData"
            />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding" eager>
            <v-divider />
            <div class="d-flex justify-end mt-4 mb-2">
              <period-picker :isDatePicker="false" @periodUpdated="updateParkingsPeriod" />
            </div>
            <LicencePlateSearchTable
              :parkingsList="searchedByLicencePlateList"
              @loadMore="loadMoreResults"
            />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider />
            <tab-info @submit="openPayoutsList()" />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider
              style="margin-left: 0px !important; margin-right: 572px"
              :class="{
                'remove-margin': !auth.isParkmanAdmin()
              }"
            />
            <data-table-reports
              v-if="reports && reports.length"
              :data="reports"
              @download="downloadReport"
            />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider
              style="margin-left: 0px !important; margin-right: 148px"
              :class="{
                'remove-margin': !(
                  auth.isParkmanAdmin() || auth.isMerchantAdmin()
                )
              }"
            />
            <tab-users
              :users="users"
              @delete="openDeleteUserForm"
              @next="openAdvancedSettings()"
            />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider />
            <tab-discounts
              :discounts="discounts"
              @edit="openEditDiscountForm"
              @view="openCountryHolidaysPopup"
              @delete="openDeleteDiscountConfirm"
              @close="isEcoCarDiscountFormOpening = false"
              @showLocations="openDiscountedLocationsForm"
            />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider />
            <tab-integrations :hubsList="hubsList" :wardensList="wardensList" />
          </v-tab-item>

          <v-tab-item class="tab-item with-horizontal-padding">
            <v-divider />
            <tab-settings
              :value="currentMerchant"
              @next="openAdvancedSettings()"
              @delete="isDeleteMerchant = true"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-row>

      <v-card
        v-if="hasChanged && (auth.isParkmanAdmin() || auth.isMerchantAdmin())"
        :elevation="6"
        class="footer"
      >
        <v-card-actions class="justify-end">
          <v-btn
            class="pkmn-button next-button"
            color="primary"
            large
            @click="saveMerchant"
          >
            {{ $t('form_save') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <form-add-payout-id v-if="false" ref="formAddPayoutId" />

      <set-report-popup
        v-if="isAddReportFormOpening"
        :merchant="currentMerchant"
        :reportSettings="reportSettings"
        :dialogShown="isAddReportFormOpening"
        @submit="isAddReportFormOpening = false"
        @close="closeSetReportForm"
      />

      <set-report-recipients-popup
        v-if="isSetReportRecipientsOpening"
        :recipientsList="recipientsList"
        :reportSettings="reportSettings"
        @close="isSetReportRecipientsOpening = false"
        @refetchRecipients="fetchRecipients"
      />

      <form-add-user
        v-if="isAddUserFormOpening"
        @submit="addUser"
        @close="isAddUserFormOpening = false"
      />

      <form-confirmation
        ref="deleteUserConfirmation"
        warning
        :title="deleteUserTitle"
        :text="this.$t('text.delete_user')"
        :cancelText="this.$t('button.back')"
        :confirmText="this.$t('button.delete')"
        @close="clearDeletingUser"
        @confirm="deleteUser"
      />

      <form-eco-car-discount
        v-if="isEcoCarDiscountFormOpening"
        :discount="selectedDiscount"
        :mode="ecoCarFormMode"
        :locationProductIds="ecoCarLocationProductIds"
        @close="closeEcoCarDiscountForm"
      />

      <form-holidays-popup
        v-if="isHolidaysFormOpening"
        :holidays="holidays"
        :discount="selectedDiscount"
        :mode="holidaysFormMode"
        :locations="zones"
        :selectedLocationIds="selectedLocationIds"
        @close="isHolidaysFormOpening = false"
        @save="saveDiscount"
      />
      <form-delete-discount
        v-if="isDeleteDiscountConfirmOpening"
        @close="isDeleteDiscountConfirmOpening = false"
        @confirm="confirmDeleteDiscount"
      />
      <form-discount-locations-popup
      v-if="isDiscountLocationsFormOpening"
      :locations="discountLocations"
      @close="isDiscountLocationsFormOpening = false"
      />
      <form-country-holidays-popup
      v-if="isCountryHolidaysPopupOpening"
      :holidays="holidays"
      :discount="selectedDiscount"
      @close="isCountryHolidaysPopupOpening = false"
      />
    </div>
  </home-template>
</template>

<script>
import auth from '@/auth'
import HomeTemplate from '@/templates/Locations'
import TabInfo from '@/components/Merchant/Tab/Merchant/MerchantInfo.vue'
import DataTableReports from '@/components/Merchant/DataTable/DataTableReports'
import TabUsers from '@/components/Merchant/Tab/Merchant/MerchantUsers.vue'
import TabSettings from '@/components/Merchant/Tab/Merchant/MerchantSettings.vue'
import TabDiscounts from '@/components/Merchant/Tab/Merchant/MerchantDiscounts.vue'
import TabSales from '@/components/Merchant/Tab/Merchant/MerchantSales.vue'
import TabMap from '@/components/Locations/AllLocations/MapForm.vue'
import TabIntegrations from '@/components/Locations/AddVisitorParking/Tab/TabIntegrations.vue'
import LicencePlateSearchTable from '@/components/Locations/AllLocations/LicencePlateSearchTable.vue'
import FormAddUser from '@/components/Merchant/Form/FormAddUser.vue'
import FormAddPayoutId from '@/components/Merchant/Form/FormAddPayoutId.vue'
import FormConfirmation from '@/components/ExitConfirmDialog.vue'
import FormEcoCarDiscount from '@/components/Merchant/Form/FormEcoCarDiscount.vue'
import FormHolidaysPopup from '../../components/Merchant/Form/FormHolidaysPopup.vue'
import FormDeleteDiscount from '../../components/Merchant/Form/FormDeleteDiscount.vue'
import FormDiscountLocationsPopup from '../../components/Merchant/Form/FormDiscountLocationsPopup.vue'
import FormCountryHolidaysPopup from '../../components/Merchant/Form/FormCountryHolidaysPopup.vue'
import PeriodPicker from '@/components/Common/PeriodPicker.vue'
import SetReportPopup from '@/components/Merchant/Form/SetReportPopup.vue'
import SetReportRecipientsPopup from '@/components/Merchant/Form/SetReportRecipientsPopup.vue'
import merchantHelpers from '@/helpers/merchant'
import * as config from '@/config/config'
import moment from 'moment'
import eventBus from '@/helpers/eventBus'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'MerchantView',
  components: {
    HomeTemplate,
    TabInfo,
    DataTableReports,
    TabUsers,
    TabSettings,
    TabSales,
    TabMap,
    FormAddUser,
    FormAddPayoutId,
    FormConfirmation,
    PeriodPicker,
    TabIntegrations,
    SetReportPopup,
    SetReportRecipientsPopup,
    TabDiscounts,
    FormEcoCarDiscount,
    FormHolidaysPopup,
    FormDeleteDiscount,
    FormDiscountLocationsPopup,
    FormCountryHolidaysPopup,
    LicencePlateSearchTable
  },
  data() {
    return {
      auth: auth,
      zones: [],
      isAddReportFormOpening: false,
      isSetReportRecipientsOpening: false,
      isAddUserFormOpening: false,
      deleteUserTitle: '',
      tmpDeletingUser: {},
      tabs: [
        {
          key: 'map',
          title: this.$t('map')
        },
        {
          key: 'sales',
          title: this.$t('sales')
        },
        {
          key: 'parkings',
          title: this.$t('g_parkings')
        },
        {
          key: 'info',
          title: this.$t('info')
        },
        {
          key: 'reports',
          title: this.$t('reports')
        },
        {
          key: 'users',
          title: this.$t('users')
        },
        {
          key: 'discounts',
          title: this.$t('discounts')
        },
        {
          key: 'integrations',
          title: this.$t('integrations'),
          hideTab: !this.showIntagrationTab
        },
        {
          key: 'advancedSettings',
          title: this.$t('advancedSettings'),
          hideTab: !auth.isParkmanAdmin()
        }
      ],
      tab: null,
      currentMerchantSnapshot: '',
      users: [],
      shouldZoomToCenter: null,
      focusedPoint: null,
      moment: moment,
      startDate: null,
      endDate: null,
      salesData: [],
      hubsList: [],
      wardensList: [],
      fetchingInProgress: false,
      reports: null,
      reportSettings: null,
      recipientsList: [],
      payoutsStartDate: null,
      payoutsEndDate: null,
      discounts: [],
      isEcoCarDiscountFormOpening: false,
      ecoCarFormMode: 'create',
      ecoCarLocationProductIds: [],
      selectedDiscount: null,
      isHolidaysFormOpening: false,
      holidaysFormMode: 'create',
      holidays: [],
      isDeleteDiscountConfirmOpening: false,
      deletingDiscount: null,
      discountLocations: [],
      isDiscountLocationsFormOpening: false,
      isCountryHolidaysPopupOpening: false,
      selectedLocationIds: []
    }
  },
  methods: {
    ...mapActions(['setSearchTabsShowing', 'setSearchByLicencePeriod']),
    openAdvancedSettings() {
      // this.step = STEP.ADVANCED_SETTINGS
    },
    async getUsers() {
      const response = await merchantHelpers.fetchAllUsersForMerchant(
        this.$route.params.merchantId
      )
      if (response) {
        const users = Array.isArray(response.data) ? response.data : []
        this.users = users
      }
    },
    openAddUserForm() {
      this.isAddUserFormOpening = true
    },
    openSetReportForm() {
      this.isAddReportFormOpening = true
    },
    openSetReportRecipientsPopup() {
      this.isSetReportRecipientsOpening = true
    },
    async addUser(user) {
      this.isAddUserFormOpening = false
      if (
        this.users.filter((theUser) => theUser.email === user.email).length > 0
      ) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: 'The user is already added'
        })
      } else {
        const addedUser = await merchantHelpers.addMerchantUser(
          this.currentMerchant.id,
          user
        )
        if (addedUser.id) {
          await this.getUsers()
          await this.$store.dispatch('setSystemMessage', {
            type: 'success',
            message: `${user.first_name} ${user.last_name} is added`
          })
        } else {
          await this.$store.dispatch('setSystemMessage', {
            type: 'error'
          })
        }
      }
    },
    openDeleteUserForm(user) {
      this.deleteUserTitle = this.$t('title.delete_user', {
        object: `${user.first_name} ${user.last_name}`
      })
      this.tmpDeletingUser = user
      this.$refs.deleteUserConfirmation.isOpening = true
    },
    closeDeleteUserForm() {
      this.deleteUserTitle = ''
    },
    async deleteUser() {
      const succeed = await merchantHelpers.deleteMerchantUser(
        this.currentMerchant.id,
        this.tmpDeletingUser.id
      )
      if (succeed) {
        await this.getUsers()
        await this.$store.dispatch('setSystemMessage', {
          type: 'delete',
          message: this.$t('removed_confirm_message', {
            object: `${this.tmpDeletingUser.first_name} ${this.tmpDeletingUser.last_name}`
          })
        })
      } else {
        await this.$store.dispatch('setSystemMessage', {
          type: 'error'
        })
      }
      this.clearDeletingUser()
    },
    clearDeletingUser() {
      this.tmpDeletingUser = {}
    },
    async saveMerchant() {
      await this.$store.dispatch('setShowOverlay', true)
      if (this.currentMerchant.vat_percentage === "") {
        this.currentMerchant.vat_percentage = null
      }
      if (this.currentMerchant.kick_back_fee_fixed === "") {
        this.currentMerchant.kick_back_fee_fixed = null
      }
      if (this.currentMerchant.kick_back_fee_fixed_level === "") {
        this.currentMerchant.kick_back_fee_fixed_level = null
      }
      if (this.currentMerchant.kick_back_fee_percentage === "") {
        this.currentMerchant.kick_back_fee_percentage = null
      }
      if (this.currentMerchant.kick_back_fee_percentage_level === "") {
        this.currentMerchant.kick_back_fee_percentage_level = null
      }
      const merchant = await merchantHelpers.updateMerchant(
        this.currentMerchant
      )
      if (!this.fetchingInProgress) {
        await this.$store.dispatch('setShowOverlay', false)
      }
      if (merchant) {
        await this.$store.dispatch('setSystemMessage', {
          message: 'Changes are updated',
          type: 'success'
        })
        this.currentMerchantSnapshot = JSON.stringify(merchant)
      }
    },
    async fetchMerchant() {
      await this.$store.dispatch('setShowOverlay', true)
      const merchant = await merchantHelpers.fetchMerchant(
        this.$route.params.merchantId
      )
      if (!merchant) {
        await this.$router.push({ name: 'AllMerchants' })
        await this.$store.dispatch('setSidebar', config.SIDEBAR.MERCHANT_ALL)
        return
      }
      this.currentMerchantSnapshot = JSON.stringify(merchant)
      if (!this.fetchingInProgress) {
        await this.$store.dispatch('setShowOverlay', false)
      }
      await this.getUsers()
    },
    async getLocations() {
      const locations = await merchantHelpers.getLocationsByMerchant(
        this.$route.params.merchantId
      )
      this.zones = locations
      await this.$store.dispatch('setMerchantLocations', locations)
      this.shouldZoomToCenter = moment()
    },
    showSearchTab() {
      // Tab switch is handled by the searchedByLicencePlateList watcher
      // to ensure data is available before activating the tab
    },
    updateParkingsPeriod({ startDate, endDate }) {
      this.setSearchByLicencePeriod({ startDate, endDate })
      eventBus.$emit('reloadMerchantLicencePlates')
    },
    async updatePeriodDates({ startDate, endDate }) {
      if (
        this.searchByLicenceStartDate !== startDate &&
        this.searchByLicenceEndDate !== endDate
      ) {
        this.setSearchByLicencePeriod({ startDate, endDate })
        eventBus.$emit('reloadMerchantLicencePlates')
      }
      await this.fetchSalesStats(startDate, endDate)
    },
    async fetchSalesStats(startDate, endDate) {
      const startTimestamp = moment.utc(startDate, 'DD.MM.YYYY').unix()
      const endTimestamp = moment.utc(endDate, 'DD.MM.YYYY').endOf('day').unix()
      const data = await merchantHelpers.fetchSalesStats(
        this.$route.params.merchantId,
        startTimestamp,
        endTimestamp
      )
      const zonesMap = new Map(
        this.zones.map((zone) => [zone.id, zone])
      )
      this.salesData = data.map((item) => {
        const zone = zonesMap.get(item.location_id)
        return {
          ...item,
          location_products: zone?.location_products || []
        }
      })
    },
    async loadReports( { startDate, endDate }) {
      this.payoutsStartDate = startDate
      this.payoutsEndDate = endDate
      const data = await merchantHelpers.fetchHistoryPayouts(
        this.$route.params.merchantId, moment.utc(startDate, 'DD.MM.YYYY').unix(), moment.utc(endDate, 'DD.MM.YYYY').endOf('day').unix()
      )
      if (data) {
        this.reports = data
      }
    },
    loadMoreResults() {
      eventBus.$emit('loadMerchantLicencePlates')
    },
    async downloadReport(reportId) {
      await merchantHelpers.download(this.$route.params.merchantId, reportId)
    },
    async exportSalesData({ selectedItems, startDate, endDate }) {
      const locationProductIds = selectedItems
        .flatMap((item) => item.location_products || [])
        .filter((lp) => lp.product_id === 1)
        .map((lp) => lp.id)
        .join(',')

      if (!locationProductIds) {
        this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: this.$t('sales_export_no_products')
        })
        return
      }

      const startTimestamp = moment.utc(startDate, 'DD.MM.YYYY').unix()
      const endTimestamp = moment.utc(endDate, 'DD.MM.YYYY').endOf('day').unix()
      await merchantHelpers.downloadSalesStats(
        this.$route.params.merchantId,
        locationProductIds,
        startTimestamp,
        endTimestamp
      )
    },
    async getWardensList() {
      this.wardensList = await merchantHelpers.getWardensList(this.wardensUrl)
    },
    async getHubsList() {
      this.hubsList = await merchantHelpers.getMerchantHubsList(
        this.$route.params.merchantId
      )
    },
    async fetchReportSettings() {
      const settings = await merchantHelpers.fetchReportSettings(
        this.$route.params.merchantId
      )
      if (settings) {
        this.reportSettings = settings[0]
      }
    },
    async fetchRecipients() {
      if (this.reportSettings) {
        const id = this.reportSettings.id
        this.recipientsList = await merchantHelpers.getReceivers(id
        )
      }
    },
    closeSetReportForm(report) {
      this.reportSettings = report
      this.isAddReportFormOpening = false
    },
    openCreateDiscountForm() {
      this.selectedDiscount = null
      this.holidays = []
      this.selectedLocationIds = []
      this.holidaysFormMode = 'create'
      this.isHolidaysFormOpening = true
    },
    openCreateVehicleDiscountForm() {
      this.selectedDiscount = null
      this.ecoCarFormMode = 'create'
      this.ecoCarLocationProductIds = []
      this.isEcoCarDiscountFormOpening = true
    },
    async closeEcoCarDiscountForm(changed) {
      this.isEcoCarDiscountFormOpening = false
      if (changed) {
        await this.fetchMerchantDiscounts()
      }
    },
    async openEditDiscountForm(discount) {
      this.selectedDiscount = discount
      if (discount.discount_type === 'eco_car_discount') {
        if ((discount.discount_level || discount.type) === 'location_product') {
          this.ecoCarLocationProductIds =
            await this.resolveEcoListLocationProductIds(
              discount.list_id || discount.discount_list_id
            )
        } else {
          this.ecoCarLocationProductIds = []
        }
        this.ecoCarFormMode = 'edit'
        this.isEcoCarDiscountFormOpening = true
      }
      if (discount.discount_type === 'holiday_discount') {
        await this.fetchHolidays(discount)
        if (discount.locations_quantity) {
          const locations = await merchantHelpers.fetchHolidayListLocations(
            this.$route.params.merchantId,
            discount.list_id || discount.discount_list_id
          )
          this.selectedLocationIds = (locations || []).map(
            (loc) => loc.location_id
          )
        } else {
          this.selectedLocationIds = []
        }
        this.holidaysFormMode = 'edit'
        this.isHolidaysFormOpening = true
      }
    },
    openDeleteDiscountConfirm(discount) {
      this.deletingDiscount = discount
      this.isDeleteDiscountConfirmOpening = true
    },
    async openCountryHolidaysPopup(discount) {
      this.selectedDiscount = discount
      await this.fetchHolidays(discount)
      this.isCountryHolidaysPopupOpening = true
    },
    async saveDiscount(payload, mode) {
      if (mode === 'edit') {
        await this.saveDiscountEdit(payload)
        return
      }
      await this.saveDiscountCreate(payload)
    },
    async saveDiscountCreate(payload) {
      const merchantId = this.$route.params.merchantId
      const listBody = {
        discount_list_name: payload.discount_list_name,
        valid_from: payload.valid_from,
        valid_to: payload.valid_to,
        discount_percentage: Number(payload.discount_percentage)
      }

      const isLocationProduct =
        payload.location_ids && payload.location_ids.length > 0
      let locationProductIds = []
      let created
      if (isLocationProduct) {
        locationProductIds = (this.zones || [])
          .filter((loc) => payload.location_ids.includes(loc.id))
          .flatMap((loc) => loc.location_products || [])
          .filter((lp) => lp.product_id === 1)
          .map((lp) => lp.id)
        created = await merchantHelpers.createLocationProductHolidayList(
          merchantId,
          {
            ...listBody,
            location_product_ids: locationProductIds
          }
        )
      } else {
        created = await merchantHelpers.createMerchantHolidayList(
          merchantId,
          listBody
        )
      }
      if (!created) return

      // Backend create endpoint returns { statusCode: 200, data: {} } with
      // no list_id in the body, so refetch the list and match by name + level
      // to find what we just created.
      let newListId = created.discount_list_id || created.list_id
      if (!newListId) {
        const allLists = await merchantHelpers.fetchHolidayDiscountLists(
          merchantId
        )
        const level = isLocationProduct ? 'location_product' : 'merchant'
        const candidates = (allLists || []).filter(
          (d) =>
            d.discount_list_name === listBody.discount_list_name &&
            (d.discount_level || d.type) === level
        )
        candidates.sort(
          (a, b) => (b.discount_list_id || 0) - (a.discount_list_id || 0)
        )
        newListId = candidates[0]?.discount_list_id
      }

      const dateStrings = (payload.holidays || [])
        .map((h) => h.holiday_date)
        .filter(Boolean)

      let addResult = null
      if (newListId && dateStrings.length > 0) {
        if (isLocationProduct && locationProductIds.length > 0) {
          addResult = await merchantHelpers.addLocationProductHolidayDates(
            merchantId,
            locationProductIds[0],
            newListId,
            dateStrings
          )
        } else {
          addResult = await merchantHelpers.addMerchantHolidayDates(
            merchantId,
            newListId,
            dateStrings
          )
        }
      }

      this.isHolidaysFormOpening = false
      await this.fetchMerchantDiscounts()
      const rejected = addResult?.rejected_dates || []
      if (rejected.length > 0) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: `List created. Some dates were rejected by the server: ${rejected.join(', ')}`
        })
      } else {
        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: 'Discount list created'
        })
      }
    },
    async saveDiscountEdit(payload) {
      const merchantId = this.$route.params.merchantId
      const discount = this.selectedDiscount
      const listId =
        payload.list_id || discount.list_id || discount.discount_list_id
      const isLocationProduct =
        (discount.discount_level || discount.type) === 'location_product'

      const originalIds = new Set(
        (this.holidays || []).map((h) => h.holiday_date_id).filter(Boolean)
      )
      const currentIds = new Set(
        payload.holidays.map((h) => h.holiday_date_id).filter(Boolean)
      )

      const idsToDelete = [...originalIds].filter((id) => !currentIds.has(id))
      const datesToAdd = payload.holidays
        .filter((h) => !h.holiday_date_id && h.holiday_date)
        .map((h) => h.holiday_date)

      for (const id of idsToDelete) {
        await merchantHelpers.deleteHolidayDate(merchantId, id, listId)
      }

      let lpIdsCache = null
      const getLpIds = async () => {
        if (lpIdsCache === null) {
          lpIdsCache = await this.resolveListLocationProductIds(listId)
        }
        return lpIdsCache
      }

      let addResult = null
      if (datesToAdd.length > 0) {
        if (isLocationProduct) {
          const lpIds = await getLpIds()
          if (lpIds.length > 0) {
            addResult = await merchantHelpers.addLocationProductHolidayDates(
              merchantId,
              lpIds[0],
              listId,
              datesToAdd
            )
          }
        } else {
          addResult = await merchantHelpers.addMerchantHolidayDates(
            merchantId,
            listId,
            datesToAdd
          )
        }
      }

      if (payload.valid_to !== discount.valid_to) {
        if (isLocationProduct) {
          const lpIds = await getLpIds()
          if (lpIds.length > 0) {
            await merchantHelpers.updateLocationProductHolidayList(
              merchantId,
              lpIds[0],
              listId,
              { valid_to: payload.valid_to }
            )
          }
        } else {
          await merchantHelpers.updateMerchantHolidayList(
            merchantId,
            listId,
            { valid_to: payload.valid_to }
          )
        }
      }

      this.isHolidaysFormOpening = false
      await this.fetchMerchantDiscounts()
      const rejected = addResult?.rejected_dates || []
      if (rejected.length > 0) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: `Some dates were rejected by the server: ${rejected.join(', ')}`
        })
      } else {
        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: 'Holiday dates updated'
        })
      }
    },
    async confirmDeleteDiscount() {
      const merchantId = this.$route.params.merchantId
      const discount = this.deletingDiscount
      const listId =
        discount.list_id ||
        discount.discount_list_id ||
        discount.discount_template_id
      const isLocationProduct =
        (discount.discount_level || discount.type) === 'location_product'

      let result
      if (discount.discount_type === 'holiday_discount') {
        if (isLocationProduct) {
          const lpIds = await this.resolveListLocationProductIds(listId)
          if (lpIds.length === 0) {
            result = false
          } else {
            const results = await Promise.all(
              lpIds.map((lpId) =>
                merchantHelpers.deleteLocationProductHolidayList(
                  merchantId,
                  lpId,
                  listId
                )
              )
            )
            result = results.every(Boolean)
          }
        } else {
          result = await merchantHelpers.deleteMerchantHolidayList(
            merchantId,
            listId
          )
        }
      } else if (isLocationProduct) {
        const lpIds = await this.resolveEcoListLocationProductIds(listId)
        if (lpIds.length === 0) {
          result = false
        } else {
          const results = await Promise.all(
            lpIds.map((lpId) =>
              merchantHelpers.deleteLocationProductEcoCarsList(
                merchantId,
                lpId,
                listId
              )
            )
          )
          result = results.every(Boolean)
        }
      } else {
        result = await merchantHelpers.deleteMerchantEcoCarsList(
          merchantId,
          listId
        )
      }
      this.isDeleteDiscountConfirmOpening = false
      if (result) {
        await this.fetchMerchantDiscounts()
        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: 'Discount list deleted'
        })
      }
      this.deletingDiscount = null
    },
    async fetchMerchantDiscounts() {
      const merchantId = this.$route.params.merchantId
      const [holidayLists, ecoCarLists] = await Promise.all([
        merchantHelpers.fetchHolidayDiscountLists(merchantId),
        merchantHelpers.fetchEcoCarsDiscountLists(merchantId)
      ])
      // mirror V2 fields onto V1 names so DataTableDiscounts and other
      // code paths that read list_id / discount_template_id / type keep working
      const toV1Shape = (d) => ({
        ...d,
        list_id: d.discount_list_id,
        discount_template_id: d.discount_list_id,
        type: d.discount_level,
        // V2 omits locations_quantity on merchant rows; V1 returned explicit null.
        locations_quantity: d.locations_quantity ?? null
      })
      const holiday = (holidayLists || []).map(toV1Shape)
      const ecoCar = (ecoCarLists || []).map((d) => ({
        ...toV1Shape(d),
        // normalize: spec examples use eco_cars_discount, schema enum uses
        // eco_car_discount — the UI branches on the singular form
        discount_type: 'eco_car_discount'
      }))
      this.discounts = [...holiday, ...ecoCar]
    },
    async resolveListLocationProductIds(listId) {
      const merchantId = this.$route.params.merchantId
      const locations = await merchantHelpers.fetchHolidayListLocations(
        merchantId,
        listId
      )
      return this.mapLocationsToLpIds(locations)
    },
    async resolveEcoListLocationProductIds(listId) {
      const merchantId = this.$route.params.merchantId
      const locations = await merchantHelpers.fetchEcoCarsListLocations(
        merchantId,
        listId
      )
      return this.mapLocationsToLpIds(locations)
    },
    mapLocationsToLpIds(locations) {
      const zonesMap = new Map((this.zones || []).map((z) => [z.id, z]))
      return (locations || [])
        .map((loc) => {
          const zone = zonesMap.get(loc.location_id)
          if (!zone) return null
          const lp = (zone.location_products || []).find(
            (x) => x.product_id === 1
          )
          return lp ? lp.id : null
        })
        .filter(Boolean)
    },
    async fetchHolidays(item) {
      const merchantId = this.$route.params.merchantId
      const listId = item.list_id || item.discount_list_id
      const isLocationProduct =
        (item.discount_level || item.type) === 'location_product'

      let holidays
      if (isLocationProduct) {
        const lpIds = await this.resolveListLocationProductIds(listId)
        holidays = lpIds.length
          ? await merchantHelpers.fetchLocationProductHolidayDates(
              merchantId,
              lpIds[0],
              listId
            )
          : []
      } else {
        holidays = await merchantHelpers.fetchMerchantHolidayDates(
          merchantId,
          listId
        )
      }
      this.holidays = Array.isArray(holidays) ? holidays : []
    },
    async fetchDiscountedLocations(item) {
      const listId = item.list_id || item.discount_template_id
      const locations =
        item.discount_type === 'holiday_discount'
          ? await merchantHelpers.fetchHolidayListLocations(
              this.$route.params.merchantId,
              listId
            )
          : await merchantHelpers.fetchEcoCarsListLocations(
              this.$route.params.merchantId,
              listId
            )
      const now = new Date()

      this.discountLocations = (locations || []).filter((loc) => {
        if (!loc.valid_to) return true
        const validToDate = new Date(loc.valid_to)
        return validToDate >= now
      })
      this.isDiscountLocationsFormOpening = true
    },
    async openDiscountedLocationsForm(item) {
      await this.fetchDiscountedLocations(item)
    }
  },
  computed: {
    ...mapGetters([
      'searchedByLicencePlateList',
      'searchByLicenceStartDate',
      'searchByLicenceEndDate',
      'filteredLocations'
    ]),
    isSalesTab() {
      return this.tabs[this.tab]?.key === 'sales'
    },
    isParkingsTab() {
      return this.tabs[this.tab]?.key === 'parkings'
    },
    hasParkingResults() {
      return this.searchedByLicencePlateList.length > 0
    },
    isReportsTab() {
      return this.tabs[this.tab]?.key === 'reports'
    },
    isUsersTab() {
      return this.tabs[this.tab]?.key === 'users'
    },
    isDiscountsTab() {
      return this.tabs[this.tab]?.key === 'discounts'
    },
    currentMerchant() {
      return this.$store.getters.currentMerchant
    },
    hasChanged() {
      return (
        this.currentMerchantSnapshot !== '' &&
        this.currentMerchantSnapshot !== JSON.stringify(this.currentMerchant)
      )
    },
    currentMerchantName() {
      if (this.currentMerchant) {
        return this.currentMerchant.name
      } else {
        return 'Loading'
      }
    },
    wardensUrl() {
      return this.$route.name === 'Merchant'
        ? auth.isParkmanAdmin()
          ? `admin/merchants/${this.$route.params.merchantId}/warden`
          : `merchants/${this.$route.params.merchantId}/warden`
        : auth.isParkmanAdmin()
          ? `admin/merchants/${this.$route.params.merchantId}/locations/${this.$route.params.locationId}/warden`
          : `merchants/${this.$route.params.merchantId}/locations/${this.$route.params.locationId}/warden`
    },
    showIntagrationTab() {
      return !!this.hubsList?.length || !!this.wardensList?.length
    }
  },
  watch: {
    async '$route.params.merchantId'() {
      this.fetchMerchant()
      this.tab = 0
      await this.getLocations()
      await Promise.all([
        this.getHubsList(),
        this.getWardensList(),
        this.fetchReportSettings(),
        this.fetchRecipients(),
        this.fetchMerchantDiscounts()
      ])
    },
    searchByLicenceStartDate(date) {
      this.startDate = date
    },
    searchByLicenceEndDate(date) {
      this.endDate = date
    },
    searchedByLicencePlateList(newList) {
      if (newList.length > 0) {
        this.$nextTick(() => {
          const parkingsIndex = this.tabs.findIndex(
            (t) => t.key === 'parkings'
          )
          if (parkingsIndex !== -1) {
            this.tab = parkingsIndex
          }
        })
      }
    },
    filteredLocations(newVal) {
      if (newVal.length) {
        this.zones = newVal
        this.shouldZoomToCenter = moment()
      } else {
        this.getLocations()
      }
    },
    async tab(tabNumber) {
      if (this.tabs[tabNumber]?.key === 'reports') {
        await this.fetchReportSettings()
        await this.fetchRecipients()
      }

      if (this.tabs[tabNumber]?.key === 'discounts') {
        await this.fetchMerchantDiscounts()
      }
    }
  },
  async created() {
    this.fetchingInProgress = true
    this.$store.dispatch('setShowOverlay', true)
    if (this.$store.getters.countries.length === 0) {
      await merchantHelpers.fetchCountries()
    }
    if (
      this.$store.getters.currentMerchant === null ||
      this.currentMerchant.id !== this.$route.params.merchantId
    ) {
      await this.fetchMerchant()
    }
    this.currentMerchantSnapshot = JSON.stringify(
      this.$store.getters.currentMerchant
    )
    if (this.zones.length === 0) {
      await this.getLocations()
    }
  },
  async mounted() {
    eventBus.$on('showSearchTab', (tabNumber) => {
      this.showSearchTab(tabNumber)
    })
    eventBus.$on('centerMapToPoint', (point) => {
      this.focusedPoint = point
    })
    this.startDate = this.searchByLicenceStartDate
    this.endDate = this.searchByLicenceEndDate
    await this.$store.dispatch('setSidebar', config.SIDEBAR.MERCHANT)

    if (this.$store.getters.countries.length === 0) {
      await merchantHelpers.fetchCountries()
    }

    if (
      this.$store.getters.currentMerchant === null ||
      this.currentMerchant.id !== this.$route.params.merchantId
    ) {
      await this.fetchMerchant()
    } else {
      await this.getUsers()
    }
    await Promise.all([this.getHubsList(), this.getWardensList()]).finally(
      () => {
        this.fetchingInProgress = false
        this.$store.dispatch('setShowOverlay', false)
      }
    )

    if (this.zones.length === 0) {
      await this.getLocations()
    }
  },
  beforeDestroy() {
    this.setSearchTabsShowing(true)
    eventBus.$off('centerMapToPoint')
  },
  destroyed() {
    this.$store.dispatch('setCurrentMerchant', null)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/style/common';
@import '~@/style/variables';
.add-user {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 122px;
  height: 38px !important;
}

.add-report-id {
  height: 38px !important;
}

.discount-button {
  height: 38px !important;
}

.v-window__container {
  height: 100%;
}

.button-panel {
  padding: 0px 68px 40px;
}

.three-buttons-gap {
  padding-right: 8px;
}

.tab-item {
  overflow-y: auto;
  transition: false;
}

.footer {
  position: absolute;
  padding: 8px 32px;
  bottom: 0;
  right: 0;
  left: $sidebar-width;
}

.next-button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 168px;
  height: 46px;
}
.map-tab {
  margin-right: 12px !important;
}

.remove-margin {
  margin-right: 0 !important;
}
</style>
